import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';
import { useSEO, SITE_URL } from '../../../hooks/useSEO';

const AccessIdentityRisksPage = () => {
  useSEO({
    title: 'Access Control & Identity Risks for Mortgage Firms | Mortgage Moat',
    description: 'Understand credential theft, privilege escalation, insider threats, and MFA bypass attacks targeting financial firms. Learn identity and access management best practices.',
    keywords: 'access control risks, identity security, credential theft, privilege escalation, insider threats, MFA security',
    canonical: '/knowledge-center/access-identity-risks',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'Access Control & Identity Risks for Financial Firms',
        description: 'Protecting your organization through proper authentication, authorization, and identity management to prevent unauthorized access to sensitive financial data.',
        url: `${SITE_URL}/knowledge-center/access-identity-risks`,
        publisher: { '@type': 'Organization', name: 'Mortgage Moat', url: SITE_URL },
        author: { '@type': 'Organization', name: 'Mortgage Moat' },
        dateModified: '2025-07-14',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: 'Knowledge Center', item: `${SITE_URL}/knowledge-center/access-identity-risks` },
          { '@type': 'ListItem', position: 3, name: 'Access & Identity Risks', item: `${SITE_URL}/knowledge-center/access-identity-risks` },
        ],
      },
    ],
  });

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-50 via-white to-gray-50 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <i className="ri-key-2-line"></i>
                Security Knowledge Center
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Access Control & <span className="text-blue-600">Identity Risks</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Protecting your organization through proper authentication, authorization, and identity management to prevent unauthorized access to sensitive financial data
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Button variant="primary" size="lg" className="whitespace-nowrap">
                  <i className="ri-lock-2-line mr-2"></i>
                  Secure Your Access
                </Button>
                <Button variant="outline" size="lg" className="whitespace-nowrap">
                  <i className="ri-file-list-3-line mr-2"></i>
                  Access Audit
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="text-center">
                <div className="text-5xl font-bold text-blue-600 mb-2">81%</div>
                <p className="text-gray-700 font-semibold">Breaches Involve Stolen Credentials</p>
                <p className="text-sm text-gray-600 mt-2">Verizon DBIR 2023</p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-blue-600 mb-2">$150K</div>
                <p className="text-gray-700 font-semibold">Average Cost of Credential Breach</p>
                <p className="text-sm text-gray-600 mt-2">For small financial firms</p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-blue-600 mb-2">50%</div>
                <p className="text-gray-700 font-semibold">Use Weak Passwords</p>
                <p className="text-sm text-gray-600 mt-2">Despite security training</p>
              </div>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="bg-blue-50 border-l-4 border-blue-600 p-6">
                <div className="flex items-start gap-4">
                  <i className="ri-shield-keyhole-line text-3xl text-blue-600 flex-shrink-0"></i>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Identity Is the New Perimeter</h3>
                    <p className="text-gray-700">
                      With remote work and cloud services, traditional network boundaries have dissolved. <strong>Identity and access management</strong> is now your primary security control, making proper authentication and authorization critical to protecting sensitive mortgage and financial data.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Threats */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Critical Access & Identity Threats</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Understanding vulnerabilities in authentication and authorization systems
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              <div className="bg-white rounded-xl shadow-lg p-8 border-t-4 border-blue-600">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-lock-unlock-line text-3xl text-blue-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Credential Theft & Compromise</h3>
                <p className="text-gray-700 mb-4">
                  Stolen usernames and passwords remain the most common attack vector, obtained through phishing, data breaches, or brute force attacks.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Password reuse:</strong> Same credentials across multiple services</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Credential stuffing:</strong> Automated login attempts with stolen credentials</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Keylogging:</strong> Malware capturing login information</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Session hijacking:</strong> Stealing active authentication tokens</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8 border-t-4 border-purple-600">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-user-settings-line text-3xl text-purple-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Privilege Escalation</h3>
                <p className="text-gray-700 mb-4">
                  Attackers exploiting vulnerabilities or misconfigurations to gain higher-level permissions than originally granted.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Vertical escalation:</strong> Standard user gaining admin rights</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Horizontal escalation:</strong> Accessing other users' data</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Misconfigured permissions:</strong> Overly broad access grants</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Orphaned accounts:</strong> Former employees retaining access</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8 border-t-4 border-red-600">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-user-forbid-line text-3xl text-red-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Insider Threats</h3>
                <p className="text-gray-700 mb-4">
                  Authorized users intentionally or accidentally misusing their legitimate access to harm the organization.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-red-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Malicious insiders:</strong> Employees stealing data for profit</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-red-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Negligent users:</strong> Accidental data exposure or loss</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-red-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Compromised insiders:</strong> Accounts controlled by attackers</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-red-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Third-party contractors:</strong> External users with internal access</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8 border-t-4 border-orange-600">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-smartphone-line text-3xl text-orange-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Multi-Factor Authentication Bypass</h3>
                <p className="text-gray-700 mb-4">
                  Sophisticated techniques attackers use to circumvent even strong authentication mechanisms.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-orange-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>MFA fatigue:</strong> Bombarding users with approval requests</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-orange-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>SIM swapping:</strong> Hijacking phone numbers for SMS codes</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-orange-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Man-in-the-middle:</strong> Intercepting authentication tokens</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-orange-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Social engineering:</strong> Tricking users into approving access</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Best Practices */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Identity & Access Management Best Practices</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Implementing robust controls to protect against unauthorized access
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6">
                <div className="w-14 h-14 bg-blue-600 text-white rounded-full flex items-center justify-center mb-4">
                  <i className="ri-shield-check-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Multi-Factor Authentication</h3>
                <p className="text-gray-700 text-sm mb-3">
                  Require MFA for all accounts, especially privileged users and remote access
                </p>
                <ul className="space-y-1 text-xs text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-0.5"></i>
                    <span>Hardware tokens or authenticator apps</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-0.5"></i>
                    <span>Avoid SMS-based authentication</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-0.5"></i>
                    <span>Conditional access policies</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6">
                <div className="w-14 h-14 bg-blue-600 text-white rounded-full flex items-center justify-center mb-4">
                  <i className="ri-key-2-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Strong Password Policies</h3>
                <p className="text-gray-700 text-sm mb-3">
                  Enforce complex passwords and regular updates with password manager usage
                </p>
                <ul className="space-y-1 text-xs text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-0.5"></i>
                    <span>Minimum 12 characters</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-0.5"></i>
                    <span>Complexity requirements</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-0.5"></i>
                    <span>Breach monitoring</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6">
                <div className="w-14 h-14 bg-blue-600 text-white rounded-full flex items-center justify-center mb-4">
                  <i className="ri-user-star-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Least Privilege Access</h3>
                <p className="text-gray-700 text-sm mb-3">
                  Grant minimum necessary permissions for users to perform their job functions
                </p>
                <ul className="space-y-1 text-xs text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-0.5"></i>
                    <span>Role-based access control</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-0.5"></i>
                    <span>Regular permission reviews</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-0.5"></i>
                    <span>Just-in-time access</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6">
                <div className="w-14 h-14 bg-blue-600 text-white rounded-full flex items-center justify-center mb-4">
                  <i className="ri-user-unfollow-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Account Lifecycle Management</h3>
                <p className="text-gray-700 text-sm mb-3">
                  Properly provision, modify, and deprovision user accounts throughout employment
                </p>
                <ul className="space-y-1 text-xs text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-0.5"></i>
                    <span>Automated onboarding/offboarding</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-0.5"></i>
                    <span>Immediate termination access removal</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-0.5"></i>
                    <span>Dormant account monitoring</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6">
                <div className="w-14 h-14 bg-blue-600 text-white rounded-full flex items-center justify-center mb-4">
                  <i className="ri-eye-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Access Monitoring & Logging</h3>
                <p className="text-gray-700 text-sm mb-3">
                  Track and analyze all authentication attempts and privileged actions
                </p>
                <ul className="space-y-1 text-xs text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-0.5"></i>
                    <span>Failed login tracking</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-0.5"></i>
                    <span>Privileged access auditing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-0.5"></i>
                    <span>Anomaly detection</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6">
                <div className="w-14 h-14 bg-blue-600 text-white rounded-full flex items-center justify-center mb-4">
                  <i className="ri-time-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Session Management</h3>
                <p className="text-gray-700 text-sm mb-3">
                  Control session duration and implement automatic timeouts for inactive users
                </p>
                <ul className="space-y-1 text-xs text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-0.5"></i>
                    <span>Automatic session expiration</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-0.5"></i>
                    <span>Concurrent session limits</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-0.5"></i>
                    <span>Secure token management</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Zero Trust */}
        <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Implementing Zero Trust Architecture</h2>
                <p className="text-lg text-gray-600">
                  "Never trust, always verify" - the modern approach to access security
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <i className="ri-shield-check-line text-blue-600"></i>
                      Core Zero Trust Principles
                    </h4>
                    <ul className="space-y-2 text-sm text-gray-700">
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-line text-blue-600 flex-shrink-0 mt-0.5"></i>
                        <span>Verify explicitly using all available data points</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-line text-blue-600 flex-shrink-0 mt-0.5"></i>
                        <span>Use least privilege access consistently</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-line text-blue-600 flex-shrink-0 mt-0.5"></i>
                        <span>Assume breach and minimize blast radius</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-line text-blue-600 flex-shrink-0 mt-0.5"></i>
                        <span>Segment access to limit lateral movement</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <i className="ri-settings-3-line text-blue-600"></i>
                      Implementation Components
                    </h4>
                    <ul className="space-y-2 text-sm text-gray-700">
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-line text-blue-600 flex-shrink-0 mt-0.5"></i>
                        <span>Identity-based access controls</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-line text-blue-600 flex-shrink-0 mt-0.5"></i>
                        <span>Device health verification</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-line text-blue-600 flex-shrink-0 mt-0.5"></i>
                        <span>Network micro-segmentation</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-line text-blue-600 flex-shrink-0 mt-0.5"></i>
                        <span>Continuous monitoring and analytics</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">
              Strengthen Your Identity Security Posture
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Our identity and access management solutions provide comprehensive protection against credential theft, unauthorized access, and insider threats tailored for financial services compliance.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button variant="outline" size="lg" className="bg-white text-blue-600 hover:bg-gray-100 border-white whitespace-nowrap">
                <i className="ri-search-line mr-2"></i>
                Access Security Audit
              </Button>
              <Button variant="primary" size="lg" className="bg-white text-blue-600 hover:bg-gray-100 whitespace-nowrap">
                <i className="ri-shield-user-line mr-2"></i>
                Implement IAM Solution
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AccessIdentityRisksPage;